// Sugalarm — Cycle 459: the treating screen's IOB card FITS on a phone
// instead of being hidden.
//
// Until this cycle the phone treating screen showed the insulin-on-board
// gauge only when the measured `.main` row was at least
// `MOBILE_STACK_MIN_H` (470) tall, and withheld it below that — so on a
// 375×812 or 360×740 phone a caregiver in a treating window never saw
// insulin on board at all. This module replaces that hide rule with a
// three-step degradation chosen once per layout pass from MEASURED room:
//
//   full     the shipped card, exactly as before
//   compact  the same card, rings at their minimum legible diameter and
//            one legend row per dose
//   chip     one row in the card's slot, labelled "Insulin on board", built
//            from `iobChip.ts`'s entries (the sheet's chip, same rules)
//
// and, only when even the chip cannot fit at full timer size, the chip
// beside the cycle-451 compact timer (operator decision, cycle 459).
// `withheld` is what remains when nothing fits even then. On the sweep set
// that happens ONLY while a check-in question is on screen on the shortest
// phones (operator decision, cycle 459), and it is a measured, pinned
// exception rather than a rule. It exists because a card that overflows
// a centred column paints over "I need help now" and takes its hit test
// (the cycle-437 dead tap); a missing instrument for the length of a
// question is the lesser harm.
//
// ⚠ NEED IS COMPUTED, SUPPLY IS MEASURED, AND NOTHING HERE READS ITS OWN
// OUTPUT. `mainH` is `.main`'s `offsetHeight`, and `.main` is the root
// column's only grower, so it is SUPPLY: nothing this module decides can
// change it (the cycle-451 fixed-point rule). The hero is measured from
// its row and caption, which no step changes either. The timer card's
// height is a CSS constant (the ring plus its padding), never a read of
// the card, because the compact timer is one of this module's OUTPUTS:
// measuring it would let the decision feed back into its own input.
//
// Kiosk and web are untouched: they keep the cycle-437 386 px rule, byte
// for byte (`full` when it fits or the row is unmeasured, else nothing).
//
// No IOB math here. What is "on board" is decided by NowScreen's
// `activeBolusesForRings` / `basalForContext`, as it always was.

import type { FrameVariant } from "../hooks/useFrameVariant";

export type IobStep = "full" | "compact" | "chip" | "withheld" | "absent";

export interface IobFit {
  step: IobStep;
  /** True when the chip needs the cycle-451 compact timer to fit. */
  compactTimer: boolean;
}

/** Cycle 437: the KIOSK instrument column's need (timer card 154 + 10 +
 *  the gauge card). Unchanged; kiosk and web read only this. */
export const KIOSK_INSTRUMENT_COLUMN_MIN_H = 386;

/** ⚠ Cycle 451 — the SHORTEST phone, with a question on screen. Moved here
 *  from `TreatingScreen` in cycle 459, value unchanged.
 *
 *  Measured on the real `MobileFrame` (webfont blocked), portrait, gauge
 *  already withheld, so the stack is the timer card + the hero:
 *
 *      timer card 138 + gap 12 + hero 101.2 = 251.2 of need
 *
 *      375x812  check-in open, plain        268.0  fits
 *      375x812  check-in open, recovering   268.0  fits
 *      360x740  check-in open, plain        229.7  short by 21.5
 *      360x740  check-in open, recovering   196.0  short by 55.2
 *
 *  Below this the timer card sheds its chrome and the ring drops to 72 (the
 *  treatment phone landscape has shipped since cycle 437, taken further in
 *  451.1), at the same 13px floor. That closes `plain` outright and cuts
 *  `recovering` to a 13.2 residual (reported, not fixed: the hero is floored
 *  at 88px, and dropping the timer or the number is not on the table).
 *
 *  ⚠ MEASURED OFF THE ROW, NEVER A HEIGHT MEDIA QUERY. A query would also
 *  compact a 360x740 phone with NO question on screen. Cycle 459 adds a
 *  second reason to compact (the IOB chip needs the room; `chooseIobFit`),
 *  and this one is unchanged: below it the timer compacts whatever the IOB
 *  step. */
export const MOBILE_TIMER_COMPACT_MIN_H = 252;

// ── The stack, as CSS lays it out on a portrait phone ────────────────────
// Each of these is a CSS value in TreatingScreen.module.css, pinned there
// by `cycle459IobCardFits.test.ts` so the two cannot drift apart.

/** `.mobile .ring` 120 + `.timerCard` padding 8×2 + border 1×2. */
export const TIMER_CARD_H = 138;
/** `.mobile.compactTimer .ring` 72; the card sheds padding and border. */
export const TIMER_COMPACT_H = 72;
/** `.mobile .main` gap, and `.mobile.compactTimer .main` gap. */
export const STACK_GAP = 12;
export const STACK_GAP_COMPACT = 6;
/** `.heroBlock` gap, and `.mobile.compactTimer .heroBlock` gap. */
export const HERO_GAP = 4;
export const HERO_GAP_COMPACT = 2;
/** Phone landscape: `.mobile .main` gap inside the `max-height: 460px`
 *  block; the slot is the row's third column there. */
export const LANDSCAPE_GAP = 24;

// ── The three steps' sizes ───────────────────────────────────────────────

/** `BolusRings`' card chrome around its canvas: padding 12 top and bottom,
 *  the half-px border, and one px more that the card always measures.
 *  Measured: 222 − 196 = 186 − 160 = 26. */
export const CARD_CHROME_H = 26;

/** The shipped gauge canvas (BolusRings' native size). */
export function fullCanvas(ringCount: number): number {
  return ringCount >= 3 ? 196 : 160;
}

/** ⚠ THE MINIMUM RING DIAMETER, BY RING COUNT, AND THE COMPACT CARD
 *  NEVER DRAWS BELOW IT. The centre stack (the `iob-ring-centre`
 *  exemption: total 18, "I.O.B" 9, ONE countdown 10; or basal-only
 *  "≈15h left" 14 over "BASAL" 9) must sit inside the innermost ring's
 *  clear radius with at least 3 px to spare. Clear radius is
 *  `(r_inner − stroke/2) × D / native`; the centre's corner is measured
 *  from its LINE BOXES (conservative: ascent and descent included) with the
 *  widest countdown the rings can show, "3h 54m 00s", on a frozen clock:
 *
 *    3 rings  r 56 of 196   clear 52k   corner 38.3 + 3   D ≥ 155.7 → 156
 *    2 rings  r 50 of 160   clear 46k   corner 38.3 + 3   D ≥ 143.7 → 144
 *    1 ring   r 66 of 160   clear 62k   corner 38.3 + 3   D ≥ 106.6 → 108
 *             (basal-only, "≈15h left": corner 34.3, margin 7.5 at 108)
 *
 *  ⚠ A first derivation used a 52 px countdown and set 140 / 130; measured
 *  on a frozen clock the countdown CROSSED the arc (−1.1 / −0.9 px). An
 *  unfrozen harness shows "01s" (its doses are dated in the past) and hides
 *  exactly this. Re-measure, frozen, before changing any centre size.
 *  The rings stay distinguishable at these sizes: the gaps between arcs are
 *  6.4 to 7.9 px at 3 rings and 7.2 px at 2. */
export const MIN_RING_DIAMETER: Readonly<Record<1 | 2 | 3, number>> = {
  1: 108,
  2: 144,
  3: 156,
};

/** Centre sizes the compact card uses. All inside the exempt centre. */
export const COMPACT_CENTRE = {
  totalPx: 18,
  labelPx: 9,
  countdownPx: 10,
  basalTotalPx: 14,
} as const;

function ringKey(ringCount: number): 1 | 2 | 3 {
  return ringCount >= 3 ? 3 : ringCount === 2 ? 2 : 1;
}

/** The compact canvas: the minimum legible diameter for this ring count,
 *  and never anything smaller. */
export function compactCanvas(ringCount: number): number {
  return MIN_RING_DIAMETER[ringKey(ringCount)];
}

/** Card heights. The legend is shorter than the canvas in both layouts
 *  (measured), so the canvas drives the card. */
export function fullCardH(ringCount: number): number {
  return fullCanvas(ringCount) + CARD_CHROME_H;
}
/** The compact card's top and bottom padding (`gauge.cardPadY`), and the
 *  chrome that leaves: 8 + 8 + the same 2 px the full card carries over its
 *  padding. Measured: 174 − 156 = 162 − 144 = 18. */
export const COMPACT_PAD_Y = 8;
export const COMPACT_CHROME_H = 2 * COMPACT_PAD_Y + 2;
export function compactCardH(ringCount: number): number {
  return compactCanvas(ringCount) + COMPACT_CHROME_H;
}

/** ⚠ The PORTRAIT width a card needs, and the row is not always that wide.
 *  A portrait card stretches to the row, so what matters is its narrowest
 *  legal width: the canvas + padding 20 left / 12 right + border + the 14px
 *  gap + the legend column. The column's binding line is the dose value on
 *  its own line (the full card's 18px indent + 95.9px for "24u · ≈15h left";
 *  the compact card's dot + gap + the same value): 113.9px, measured. So
 *  canvas + 161, for both steps. A three-ring full card needs 357: it fits
 *  a 390-wide phone (row 358, as it always has) and not a 375 (row 343),
 *  where "LONG-ACTING" would be cut off. */
export const PORTRAIT_SIDE_W = 161;

/** Card WIDTHS, for the landscape column only (a portrait card stretches
 *  to the row). In that column the card sizes to its content and its
 *  "INSULIN ON BOARD" label is the widest thing beside the rings (the
 *  legend is held to `min-content` there). Measured on the fallback font,
 *  full and compact at every ring count: canvas + 190 (386, 350, 330, 320).
 *  Two px of margin. */
export const CARD_SIDE_W = 192;
export function fullCardW(ringCount: number): number {
  return fullCanvas(ringCount) + CARD_SIDE_W;
}
export function compactCardW(ringCount: number): number {
  return compactCanvas(ringCount) + CARD_SIDE_W;
}

/** The chip. Portrait: a two-column grid whose first cell is the label, so
 *  one entry is one row and two or three entries are two rows. Landscape:
 *  one column, the label then one row per entry. Row 22 (the ring), gap 4,
 *  padding 8 + 8, border 1 + 1. */
export const CHIP_ROW_H = 22;
export const CHIP_ROW_GAP = 4;
export const CHIP_CHROME_H = 18;
export function chipRows(entryCount: number, landscape: boolean): number {
  const n = Math.max(1, Math.min(3, entryCount));
  return landscape ? n + 1 : Math.ceil((n + 1) / 2);
}
export function chipH(entryCount: number, landscape: boolean): number {
  const rows = chipRows(entryCount, landscape);
  return rows * CHIP_ROW_H + (rows - 1) * CHIP_ROW_GAP + CHIP_CHROME_H;
}
/** The landscape chip's column width: the widest line ("Insulin on board"
 *  or a 13px dose with its ring) plus 8px sides. Measured 142 with the
 *  longest fixture line ("24u · ≈15h left"); four px of margin. ⚠ A 740x360
 *  phone with a No Data hero leaves the column exactly 150, so this cannot
 *  grow by more than 4 without withholding the chip there (the sweep's
 *  largest-fit proof fails if it does). */
export const CHIP_LANDSCAPE_W = 146;

// ── The chooser ──────────────────────────────────────────────────────────

/** What the screen measured this layout pass. Every field is a quantity no
 *  IOB step can change (see the header). */
export interface IobRoom {
  /** `.main` `offsetHeight` — supply. */
  mainH: number;
  /** `.main` `offsetWidth` — supply, read in landscape only. */
  mainW: number;
  /** `.heroRow` + `.heroCaption` heights (the hero without its gap). */
  heroH: number;
  /** The hero's natural width (landscape only). */
  heroW: number;
  /** The timer card's width (landscape only; `flex: 0 0 auto` there). */
  timerW: number;
}

export interface IobFitInput {
  variant: FrameVariant;
  /** Anything on board: the card's own gate, `boluses.length > 0 ||
   *  basal != null`. */
  onBoard: boolean;
  /** Rings the gauge draws (boluses + a basal). */
  ringCount: number;
  /** Rows the chip draws (`iobChipEntries`, plus a due long-acting). */
  entryCount: number;
  landscape: boolean;
  /** Null until the first measurement (or where nothing can measure). */
  room: IobRoom | null;
}

const NONE: IobFit = { step: "absent", compactTimer: false };

/** The portrait stack's need with the card at `cardH`. */
export function portraitNeed(heroH: number, cardH: number, compactTimer: boolean): number {
  return compactTimer
    ? TIMER_COMPACT_H + STACK_GAP_COMPACT + heroH + HERO_GAP_COMPACT + STACK_GAP_COMPACT + cardH
    : TIMER_CARD_H + STACK_GAP + heroH + HERO_GAP + STACK_GAP + cardH;
}

/** The width the landscape row leaves for the card's column. */
export function landscapeRoomW(room: IobRoom): number {
  return room.mainW - room.heroW - room.timerW - 2 * LANDSCAPE_GAP;
}

export function chooseIobFit(i: IobFitInput): IobFit {
  if (!i.onBoard) return NONE;
  const room = i.room;
  const unknown = room == null || !(room.mainH > 0);

  // Kiosk and web: the cycle-437 rule, unchanged. An unmeasured row shows
  // the card (640 has the room; a short stage hides it on the next frame).
  if (i.variant !== "mobile") {
    if (unknown) return { step: "full", compactTimer: false };
    return {
      step: room!.mainH >= KIOSK_INSTRUMENT_COLUMN_MIN_H ? "full" : "withheld",
      compactTimer: false,
    };
  }

  // ⚠ An unmeasured PHONE row withholds, for exactly one frame. Showing a
  // card before the row is known is what let the gauge cover and take the
  // tap from "I need help now" (cycle 437); the ResizeObserver reports on
  // the next frame and the step follows.
  if (unknown) return { step: "withheld", compactTimer: false };
  const r = room!;

  if (i.landscape) {
    const w = landscapeRoomW(r);
    if (fullCardH(i.ringCount) <= r.mainH && fullCardW(i.ringCount) <= w) {
      return { step: "full", compactTimer: false };
    }
    if (compactCardH(i.ringCount) <= r.mainH && compactCardW(i.ringCount) <= w) {
      return { step: "compact", compactTimer: false };
    }
    if (chipH(i.entryCount, true) <= r.mainH && CHIP_LANDSCAPE_W <= w) {
      return { step: "chip", compactTimer: false };
    }
    return { step: "withheld", compactTimer: false };
  }

  // Portrait. The cycle-451 rule already compacts the timer on a row this
  // short; then only the chip can be tried, beside the compact timer.
  const timerAlreadyCompact = r.mainH < MOBILE_TIMER_COMPACT_MIN_H;
  if (!timerAlreadyCompact) {
    const steps: Array<[IobStep, number, number]> = [
      ["full", fullCardH(i.ringCount), fullCanvas(i.ringCount) + PORTRAIT_SIDE_W],
      ["compact", compactCardH(i.ringCount), compactCanvas(i.ringCount) + PORTRAIT_SIDE_W],
      ["chip", chipH(i.entryCount, false), 0],
    ];
    for (const [step, h, w] of steps) {
      if (portraitNeed(r.heroH, h, false) <= r.mainH && w <= r.mainW) {
        return { step, compactTimer: false };
      }
    }
  }
  if (portraitNeed(r.heroH, chipH(i.entryCount, false), true) <= r.mainH) {
    return { step: "chip", compactTimer: true };
  }
  return { step: "withheld", compactTimer: false };
}
